/**
 * Validation Utilities Module
 * Common validation functions used across the application with type safety
 */

interface ValidationResult {
    valid: boolean;
    message?: string;
}

interface Validator {
    validate: (value: string | null | undefined) => ValidationResult;
}

interface FormValidationResult {
    valid: boolean;
    errors: Array<{ field: string; message: string }>;
}

type ValidatorRules = Record<string, Validator[]>;

/**
 * ValidationUtils class providing static validation methods
 */
export class ValidationUtils {
    /**
     * Validate IPv4 address
     * @param ip - IP address string to validate
     * @returns True if valid IPv4 address
     */
    static isValidIP(ip: string): boolean {
        const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
        if (!ipPattern.test(ip)) return false;

        const parts = ip.split('.').map(Number);
        return parts.every(part => part >= 0 && part <= 255);
    }

    /**
     * Validate CIDR notation
     * @param cidr - CIDR notation string (e.g., "192.168.1.0/24")
     * @returns True if valid CIDR notation
     */
    static isValidCIDR(cidr: string): boolean {
        const cidrPattern = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;
        if (!cidrPattern.test(cidr)) return false;

        const [ip, mask] = cidr.split('/');
        const maskNum = parseInt(mask, 10);

        if (maskNum < 0 || maskNum > 32) return false;
        return ValidationUtils.isValidIP(ip);
    }

    /**
     * Validate IP range (e.g., "192.168.1.1-192.168.1.100")
     * @param range - IP range string
     * @returns True if valid IP range
     */
    static isValidIPRange(range: string): boolean {
        const rangePattern = /^(\d{1,3}\.){3}\d{1,3}-(\d{1,3}\.){3}\d{1,3}$/;
        if (!rangePattern.test(range)) return false;

        const [startIP, endIP] = range.split('-');
        return ValidationUtils.isValidIP(startIP) && ValidationUtils.isValidIP(endIP);
    }

    /**
     * Validate IP input (single IP, CIDR, range, or comma-separated IPs)
     * @param input - IP input string
     * @returns True if valid IP input
     */
    static isValidIPInput(input: string | null | undefined): boolean {
        if (!input || typeof input !== 'string') return false;

        const trimmedInput = input.trim();

        // Single IP
        if (ValidationUtils.isValidIP(trimmedInput)) return true;

        // CIDR notation
        if (trimmedInput.includes('/')) {
            return ValidationUtils.isValidCIDR(trimmedInput);
        }

        // IP range
        if (trimmedInput.includes('-')) {
            return ValidationUtils.isValidIPRange(trimmedInput);
        }

        // Comma-separated IPs
        if (trimmedInput.includes(',')) {
            const ips = trimmedInput.split(',').map(ip => ip.trim());
            return ips.every(ip => ValidationUtils.isValidIP(ip));
        }

        return false;
    }

    /**
     * Validate email address
     * @param email - Email address string
     * @returns True if valid email
     */
    static isValidEmail(email: string): boolean {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailPattern.test(email);
    }

    /**
     * Validate URL
     * @param url - URL string
     * @returns True if valid URL
     */
    static isValidURL(url: string): boolean {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Validate port number
     * @param port - Port number (string or number)
     * @returns True if valid port (1-65535)
     */
    static isValidPort(port: string | number): boolean {
        const portNum = typeof port === 'string' ? parseInt(port, 10) : port;
        return !isNaN(portNum) && portNum >= 1 && portNum <= 65535;
    }

    /**
     * Validate hostname
     * @param hostname - Hostname string
     * @returns True if valid hostname
     */
    static isValidHostname(hostname: string): boolean {
        const hostnamePattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        return hostnamePattern.test(hostname) && hostname.length <= 253;
    }

    /**
     * Validate MAC address
     * @param mac - MAC address string
     * @returns True if valid MAC address
     */
    static isValidMACAddress(mac: string): boolean {
        const macPattern = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
        return macPattern.test(mac);
    }

    /**
     * Validate an entire form based on rules
     * @param formElement - The form element to validate
     * @param rules - Validation rules for each field
     * @returns Validation result with errors
     */
    static validateForm(formElement: HTMLFormElement, rules: ValidatorRules): FormValidationResult {
        const errors: Array<{ field: string; message: string }> = [];
        const formData = new FormData(formElement);

        for (const [fieldName, validators] of Object.entries(rules)) {
            const value = formData.get(fieldName) as string | null;

            for (const validator of validators) {
                const result = validator.validate(value);
                if (!result.valid) {
                    errors.push({
                        field: fieldName,
                        message: result.message || `Invalid ${fieldName}`
                    });
                }
            }
        }

        return {
            valid: errors.length === 0,
            errors
        };
    }

    // Validator factories

    /**
     * Create a required field validator
     * @param message - Custom error message
     * @returns Validator instance
     */
    static required(message: string = 'This field is required'): Validator {
        return {
            validate: (value: string | null | undefined): ValidationResult => ({
                valid: value !== null && value !== undefined && value.toString().trim() !== '',
                message
            })
        };
    }

    /**
     * Create a minimum length validator
     * @param min - Minimum length
     * @param message - Custom error message
     * @returns Validator instance
     */
    static minLength(min: number, message?: string): Validator {
        return {
            validate: (value: string | null | undefined): ValidationResult => ({
                valid: !value || value.toString().length >= min,
                message: message || `Must be at least ${min} characters`
            })
        };
    }

    /**
     * Create a maximum length validator
     * @param max - Maximum length
     * @param message - Custom error message
     * @returns Validator instance
     */
    static maxLength(max: number, message?: string): Validator {
        return {
            validate: (value: string | null | undefined): ValidationResult => ({
                valid: !value || value.toString().length <= max,
                message: message || `Must be no more than ${max} characters`
            })
        };
    }

    /**
     * Create a regex pattern validator
     * @param regex - Regular expression pattern
     * @param message - Custom error message
     * @returns Validator instance
     */
    static pattern(regex: RegExp, message: string = 'Invalid format'): Validator {
        return {
            validate: (value: string | null | undefined): ValidationResult => ({
                valid: !value || regex.test(value),
                message
            })
        };
    }

    /**
     * Create an IP address validator
     * @param message - Custom error message
     * @returns Validator instance
     */
    static ip(message: string = 'Invalid IP address'): Validator {
        return {
            validate: (value: string | null | undefined): ValidationResult => ({
                valid: !value || ValidationUtils.isValidIP(value),
                message
            })
        };
    }

    /**
     * Create an IP input validator (IP, CIDR, or range)
     * @param message - Custom error message
     * @returns Validator instance
     */
    static ipInput(message: string = 'Invalid IP address, CIDR, or IP range'): Validator {
        return {
            validate: (value: string | null | undefined): ValidationResult => ({
                valid: !value || ValidationUtils.isValidIPInput(value),
                message
            })
        };
    }

    /**
     * Create an email validator
     * @param message - Custom error message
     * @returns Validator instance
     */
    static email(message: string = 'Invalid email address'): Validator {
        return {
            validate: (value: string | null | undefined): ValidationResult => ({
                valid: !value || ValidationUtils.isValidEmail(value),
                message
            })
        };
    }

    /**
     * Create a URL validator
     * @param message - Custom error message
     * @returns Validator instance
     */
    static url(message: string = 'Invalid URL'): Validator {
        return {
            validate: (value: string | null | undefined): ValidationResult => ({
                valid: !value || ValidationUtils.isValidURL(value),
                message
            })
        };
    }

    /**
     * Create a port validator
     * @param message - Custom error message
     * @returns Validator instance
     */
    static port(message: string = 'Invalid port number (1-65535)'): Validator {
        return {
            validate: (value: string | null | undefined): ValidationResult => ({
                valid: !value || ValidationUtils.isValidPort(value),
                message
            })
        };
    }

    /**
     * Create a hostname validator
     * @param message - Custom error message
     * @returns Validator instance
     */
    static hostname(message: string = 'Invalid hostname'): Validator {
        return {
            validate: (value: string | null | undefined): ValidationResult => ({
                valid: !value || ValidationUtils.isValidHostname(value),
                message
            })
        };
    }

    // UI helper methods

    /**
     * Display validation errors in the UI
     * @param errors - Array of error objects
     * @param containerSelector - CSS selector for error container
     */
    static displayErrors(
        errors: Array<{ field: string; message: string }>,
        containerSelector: string = '.validation-errors'
    ): void {
        const container = document.querySelector<HTMLElement>(containerSelector);
        if (!container) return;

        if (errors.length === 0) {
            container.innerHTML = '';
            container.classList.add('hidden');
            return;
        }

        const errorHtml = `
            <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                <div class="flex">
                    <i data-feather="alert-circle" class="h-5 w-5 text-red-600 mr-3"></i>
                    <div>
                        <h3 class="text-sm font-medium text-red-800">Validation Errors</h3>
                        <ul class="mt-2 text-sm text-red-700 list-disc list-inside">
                            ${errors.map(error => `<li>${this.escapeHtml(error.message)}</li>`).join('')}
                        </ul>
                    </div>
                </div>
            </div>
        `;

        container.innerHTML = errorHtml;
        container.classList.remove('hidden');
    }

    /**
     * Clear validation errors from the UI
     * @param containerSelector - CSS selector for error container
     */
    static clearErrors(containerSelector: string = '.validation-errors'): void {
        const container = document.querySelector<HTMLElement>(containerSelector);
        if (container) {
            container.innerHTML = '';
            container.classList.add('hidden');
        }

        // Remove field-level error styling
        document.querySelectorAll<HTMLElement>('.border-red-300').forEach(field => {
            field.classList.remove('border-red-300');
            field.classList.add('border-gray-300');
        });
    }

    /**
     * Highlight fields with errors
     * @param errors - Array of error objects
     */
    static highlightFieldErrors(errors: Array<{ field: string; message: string }>): void {
        // Clear previous error styling
        ValidationUtils.clearErrors();

        errors.forEach(error => {
            const field = document.querySelector<HTMLElement>(`[name="${error.field}"]`);
            if (field) {
                field.classList.remove('border-gray-300');
                field.classList.add('border-red-300');
            }
        });
    }

    /**
     * Setup real-time validation for form fields
     * @param formElement - The form element
     * @param rules - Validation rules
     */
    static setupRealTimeValidation(formElement: HTMLFormElement, rules: ValidatorRules): void {
        Object.keys(rules).forEach(fieldName => {
            const field = formElement.querySelector<HTMLInputElement>(`[name="${fieldName}"]`);
            if (!field) return;

            field.addEventListener('blur', () => {
                const fieldRules: ValidatorRules = { [fieldName]: rules[fieldName] };
                const result = ValidationUtils.validateForm(formElement, fieldRules);

                if (result.valid) {
                    field.classList.remove('border-red-300');
                    field.classList.add('border-gray-300');
                } else {
                    field.classList.remove('border-gray-300');
                    field.classList.add('border-red-300');
                }
            });
        });
    }

    /**
     * Escape HTML to prevent XSS
     * @param text - Text to escape
     * @returns Escaped HTML string
     */
    private static escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

// Export to global scope
declare global {
    interface Window {
        ValidationUtils: typeof ValidationUtils;
    }
}

window.ValidationUtils = ValidationUtils;

// Export for ES6 modules
